import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container, Box, IconButton, Typography, Button, Grid, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, CircularProgress, TextField, Rating, List, ListItem, ListItemText } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useSalonsRetrieveQuery } from '../../../services/GLOWClient';
import SalonManagement from './salonManagement';
import AddSalon from './addSalon';
import theme from '../../../theme';
import { BASE_URL } from '../../../config';
import BusinessIcon from '@mui/icons-material/Business';
import { Card, CardContent } from '@mui/material';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import TranslateIcon from '@mui/icons-material/Translate';
import PaletteIcon from '@mui/icons-material/Palette';
import { useLazySalonQrRetrieveQuery } from '../../../services/salonsApi';
import ReviewService from '../../../services/review.service';
import TranslationValidationModal from './TranslationValidationModal';
import { getLocalizedField } from '../../../utils/localizedFields';
import { useSalonAccess } from '../../../context/SalonAccessContext';
import { shouldOpenSalonPhotoEditor } from './salonPhotoEditQuery';

const SalonDetail = () => {
  const { t, i18n } = useTranslation();
  const { salonId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useSalonAccess();
  const canManageTheme = hasPermission(
    Number(salonId),
    'salon.manage_theme',
  );

  const getLocalizedName = (salon) => {
    return getLocalizedField(salon, 'name', i18n.language);
  };

  const getLocalizedDescription = (salon) => {
    return getLocalizedField(salon, 'description', i18n.language);
  };

  // Helper function to get localized address
  const getLocalizedAddress = (salon) => {
    if (!salon) return '';
    
    // First try localized_address if available
    if (salon.localized_address) {
      return salon.localized_address;
    }
    
    // Fallback to formatted_address
    return salon.formatted_address || t('pro_dashboard.salon.not_specified');
  };
  const autoOpenedReviewsRef = useRef(false);
  const autoOpenedPhotoEditorRef = useRef(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [focusEditDialogPhotos, setFocusEditDialogPhotos] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [reviewsDialogOpen, setReviewsDialogOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState(null);

  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [fetchQr, { data: qrData, isFetching: isQrLoading, error: qrError }] = useLazySalonQrRetrieveQuery();

  const [translationModalOpen, setTranslationModalOpen] = useState(false);
  const [translationModalLoading, setTranslationModalLoading] = useState(false);
  const [salonTranslations, setSalonTranslations] = useState(null);

  const handleOpenTranslations = async () => {
    setTranslationModalOpen(true);
    setTranslationModalLoading(true);
    setSalonTranslations(null);
    try {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      const response = await fetch(`/api/salons/${salonId}/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSalonTranslations({
          name: data.name_translations || {},
          description: data.description_translations || {},
        });
      }
    } catch (e) {
      console.warn('[translations] fetch error', e);
    } finally {
      setTranslationModalLoading(false);
    }
  };

  const handleSaveTranslations = async (editedTranslations) => {
    setTranslationModalOpen(false);
    try {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      await fetch(`/api/salons/${salonId}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name_translations: editedTranslations.name,
          description_translations: editedTranslations.description,
          translations_auto: false,
        }),
      });
    } catch (e) {
      console.warn('[translations] patch error', e);
    }
  };

  const publicUrl = useMemo(() => qrData?.public_url || '', [qrData]);
  const qrDataUri = useMemo(() => qrData?.qr_code_data_uri || '', [qrData]);


  // Fetch salon data
  const { data: salon, isLoading, error, refetch } = useSalonsRetrieveQuery(
    { salonId: parseInt(salonId), language: i18n.language },
    { skip: !salonId }
  );

  // Force re-render of localized content when language changes
  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [i18n.language]);

  // Memoized localized name to force re-render when language changes
  const localizedName = useMemo(() => {
    return getLocalizedName(salon);
  }, [salon, i18n.language, refreshKey]);

  // Auto-refresh when language changes
  useEffect(() => {
    if (salonId) {
      console.log('[SalonDetail] Language changed to:', i18n.language, ' - refetching salon data');
      refetch();
    }
  }, [i18n.language, salonId, refetch]);

  const handleBack = () => {
    navigate('/pro/salon');
  };

  const handleCloseNotification = (event, reason) => {
    if (reason === 'clickaway') return;
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const handleOpenQr = async () => {
    setQrDialogOpen(true);
    const id = parseInt(salonId);
    if (!Number.isNaN(id)) {
      fetchQr(id);
    }
  };

  useEffect(() => {
    if (autoOpenedReviewsRef.current) return;

    const shouldOpen = new URLSearchParams(location.search).get('openReviews') === 'true';
    if (!shouldOpen) return;
    if (!salon?.salon_id) return;

    autoOpenedReviewsRef.current = true;
    handleOpenReviews();
  }, [location.search, salon?.salon_id]);

  useEffect(() => {
    if (autoOpenedPhotoEditorRef.current) return;
    if (!shouldOpenSalonPhotoEditor(location.search, salon?.salon_id)) return;

    autoOpenedPhotoEditorRef.current = true;
    setFocusEditDialogPhotos(true);
    setShowEditDialog(true);
  }, [location.search, salon?.salon_id]);

  const toSlug = (value) => {
    if (!value) return 'salon';
    return value
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
      .slice(0, 60) || 'salon';
  };

  const qrDownloadFilename = useMemo(() => {
    const namePart = toSlug(getLocalizedName(salon));
    return `${namePart}_qr.png`;
  }, [salon, i18n.language]);

  const handleOpenReviews = async () => {
    if (!salon?.salon_id) return;
    setReviewsDialogOpen(true);
    setReviewsLoading(true);
    setReviewsError(null);
    try {
      const data = await ReviewService.getSalonReviews(salon.salon_id);
      const items = Array.isArray(data) ? data : data?.results || [];
      setReviews(items);
    } catch (error) {
      console.error('Error loading reviews in SalonDetail:', error);
      setReviewsError(t('pro_dashboard.salon.reviews_load_error'));
    } finally {
      setReviewsLoading(false);
    }
  };

  const handleCloseReviews = () => {
    setReviewsDialogOpen(false);
  };

  const handleCopyPublicUrl = async () => {
    try {
      if (!publicUrl) return;
      await navigator.clipboard.writeText(publicUrl);
      setNotification({ open: true, message: t('pro_dashboard.salon.link_copied'), severity: 'success' });
    } catch (e) {
      setNotification({ open: true, message: t('pro_dashboard.salon.link_copy_error'), severity: 'error' });
    }
  };

  // Get primary photo URL
  const getPrimaryPhoto = () => {
    if (!salon?.photos) return null;

    if (Array.isArray(salon.photos)) {
      const primaryPhoto = salon.photos.find(photo => photo.is_primary);
      const photoUrl = primaryPhoto?.url || salon.photos[0]?.url || null;

      if (photoUrl) {
        return photoUrl.startsWith('http') ? photoUrl : `${BASE_URL}${photoUrl}`;
      }
    }

    return null;
  };

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Typography>{t('pro_dashboard.salon.loading')}</Typography>
      </Container>
    );
  }

  if (error || !salon) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Typography color="error">{t('pro_dashboard.salon.load_error')}</Typography>
      </Container>
    );
  }

  const primaryPhotoUrl = getPrimaryPhoto();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.colors.background.secondary }}>
      {/* Header with salon info */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: 'auto', md: 350 },
          backgroundImage: primaryPhotoUrl
            ? `url(${primaryPhotoUrl})`
            : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundColor: primaryPhotoUrl ? 'transparent' : theme.colors.primary[500],
          mb: 3
        }}
      >
        {/* Gradient overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: primaryPhotoUrl
              ? `linear-gradient(135deg, ${theme.colors.primary[600]}CC 0%, ${theme.colors.secondary[600]}CC 100%)`
              : `linear-gradient(135deg, ${theme.colors.primary[600]} 0%, ${theme.colors.secondary[700]} 100%)`,
            zIndex: 1
          }}
        />

        {/* Content */}
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2, height: '100%', px: { xs: 2, sm: 3 } }}>
          <Box sx={{ pt: { xs: 2, md: 2 }, pb: { xs: 2, md: 3 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            {/* Top bar: back button and edit button */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: { xs: 1.5, md: 2 }, flexWrap: 'wrap', gap: 1 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                sx={{
                  bgcolor: 'rgba(0, 0, 0, 0.3)',
                  color: 'white',
                  backdropFilter: 'blur(10px)',
                  px: 2.5,
                  py: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.4)'
                  }
                }}
              >
                {t('pro_dashboard.salon.back')}
              </Button>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <IconButton
                  onClick={handleOpenQr}
                  sx={{
                    display: { xs: 'inline-flex', md: 'none' },
                    bgcolor: theme.colors.secondary[300],
                    color: theme.colors.primary[900],
                    borderRadius: 2,
                    border: `1px solid ${theme.colors.secondary[400]}`,
                    boxShadow: '0 6px 18px rgba(0, 0, 0, 0.18)',
                    width: 44,
                    height: 44,
                    '&:hover': {
                      bgcolor: theme.colors.secondary[400],
                      boxShadow: '0 10px 24px rgba(0, 0, 0, 0.22)'
                    }
                  }}
                  aria-label={t('pro_dashboard.salon.qr_code')}
                >
                  <QrCode2Icon />
                </IconButton>

                <Button
                  variant="outlined"
                  onClick={handleOpenQr}
                  startIcon={<QrCode2Icon />}
                  sx={{
                    display: { xs: 'none', md: 'inline-flex' },
                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                    color: 'white',
                    borderColor: 'rgba(255,255,255,0.45)',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255, 255, 255, 0.22)'
                    }
                  }}
                >
                  {t('pro_dashboard.salon.qr_code')}
                </Button>

                {/* Translation button - mobile */}
                <IconButton
                  onClick={handleOpenTranslations}
                  sx={{
                    display: { xs: 'inline-flex', md: 'none' },
                    bgcolor: theme.colors.secondary[300],
                    color: theme.colors.primary[900],
                    borderRadius: 2,
                    border: `1px solid ${theme.colors.secondary[400]}`,
                    boxShadow: '0 6px 18px rgba(0, 0, 0, 0.18)',
                    width: 44,
                    height: 44,
                    '&:hover': {
                      bgcolor: theme.colors.secondary[400],
                      boxShadow: '0 10px 24px rgba(0, 0, 0, 0.22)'
                    }
                  }}
                  aria-label={t('pro_salon.translations.see_translations', 'Voir traductions')}
                >
                  <TranslateIcon />
                </IconButton>

                {/* Translation button - desktop */}
                <Button
                  variant="outlined"
                  onClick={handleOpenTranslations}
                  startIcon={<TranslateIcon />}
                  sx={{
                    display: { xs: 'none', md: 'inline-flex' },
                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                    color: 'white',
                    borderColor: 'rgba(255,255,255,0.45)',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255, 255, 255, 0.22)'
                    }
                  }}
                >
                  {t('pro_salon.translations.see_translations', 'Voir traductions')}
                </Button>

                <Button
                  variant="outlined"
                  onClick={handleOpenReviews}
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                    color: 'white',
                    borderColor: 'rgba(255,255,255,0.45)',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255, 255, 255, 0.22)'
                    }
                  }}
                >
                  {t('pro_dashboard.salon.view_reviews')}
                </Button>

                {canManageTheme && (
                  <Button
                    variant="outlined"
                    onClick={() => navigate(`/pro/salon/${salonId}/theme`)}
                    startIcon={<PaletteIcon />}
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.15)',
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.45)',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: 'white',
                        bgcolor: 'rgba(255, 255, 255, 0.22)',
                      },
                    }}
                  >
                    {t('salon_theme.actions.open')}
                  </Button>
                )}

                <Button
                  variant="contained"
                  onClick={() => {
                    setFocusEditDialogPhotos(false);
                    setShowEditDialog(true);
                  }}
                  sx={{
                    bgcolor: theme.colors.secondary[300],
                    color: theme.colors.primary[900],
                    fontWeight: 600,
                    px: { xs: 1, md: 2.5 },
                    py: 1,
                    minWidth: { xs: 40, md: 'auto' },
                    textTransform: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: { xs: 0, md: 1 },
                    '&:hover': {
                      bgcolor: theme.colors.secondary[400]
                    }
                  }}
                >
                  <EditIcon />
                  <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                    {t('pro_dashboard.salon.edit')}
                  </Box>
                </Button>
              </Box>
            </Box>

            {/* Salon details */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 1.5 } }}>
              {/* Salon name */}
              <Typography
                variant="h3"
                sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  textShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  fontSize: { xs: '1.75rem', md: '2.5rem' },
                  lineHeight: { xs: 1.2, md: 1.3 },
                  mb: { xs: 1, md: 0 }
                }}
              >
                {localizedName}
              </Typography>

              {/* Salon description - only if description exists */}
              {getLocalizedDescription(salon) && (
                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontWeight: 400,
                    textShadow: '0 1px 4px rgba(0,0,0,0.3)',
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    lineHeight: 1.5,
                    mb: { xs: 2, md: 1.5 },
                    maxWidth: { xs: '100%', md: '90%' }
                  }}
                >
                  {getLocalizedDescription(salon)}
                </Typography>
              )}

              {/* MOBILE VIEW - Show only name and centered "See More" button */}
              <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5, alignItems: 'center', width: '100%' }}>
                {/* "See More" button for mobile - centered */}
                <Button
                  variant="outlined"
                  onClick={() => setShowDetailsDialog(true)}
                  sx={{
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  {t('pro_dashboard.salon.view_more')}
                </Button>
              </Box>

              {/* DESKTOP VIEW - Show address, phone, and hours */}
              <Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: '1fr 1fr', gap: 3, alignItems: 'flex-start' }}>
                {/* Left: Address and Phone */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {/* Address with icon */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <LocationOnIcon sx={{ color: 'white', mt: 0.3, fontSize: '1.2rem', flexShrink: 0 }} />
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'white',
                        fontWeight: 400,
                        textShadow: '0 1px 4px rgba(0,0,0,0.3)',
                        wordWrap: 'break-word',
                        fontSize: '1rem',
                        lineHeight: 1.4
                      }}
                    >
                      {getLocalizedAddress(salon)}
                    </Typography>
                  </Box>

                  {/* Phone with icon */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <PhoneIcon sx={{ color: 'white', mt: 0.3, fontSize: '1.2rem', flexShrink: 0 }} />
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'white',
                        fontWeight: 400,
                        textShadow: '0 1px 4px rgba(0,0,0,0.3)',
                        fontSize: '1rem',
                        lineHeight: 1.4
                      }}
                    >
                      {salon.phone || t('pro_dashboard.salon.not_specified')}
                    </Typography>
                  </Box>
                </Box>

                {/* Right: Opening hours */}
                {salon.opening_hours && (
                  <Box
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.15)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: 2,
                      p: { xs: 2.5, md: 2 },
                      display: 'flex',
                      flexDirection: 'column',
                      gap: { xs: 1.5, md: 1 }
                    }}
                  >
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 1, md: 0.5 } }}>
                      <AccessTimeIcon sx={{ color: 'white', fontSize: { xs: '1.1rem', md: '1.2rem' } }} />
                      <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.95rem', md: '0.875rem' } }}>
                        {t('pro_dashboard.salon.opening_hours')}
                      </Typography>
                    </Box>

                    {(() => {
                      const allDays = [
                        { key: 'lundi', label: t('weekdays.monday') },
                        { key: 'mardi', label: t('weekdays.tuesday') },
                        { key: 'mercredi', label: t('weekdays.wednesday') },
                        { key: 'jeudi', label: t('weekdays.thursday') },
                        { key: 'vendredi', label: t('weekdays.friday') },
                        { key: 'samedi', label: t('weekdays.saturday') },
                        { key: 'dimanche', label: t('weekdays.sunday') }
                      ];

                      // Separate open and closed days
                      const openDays = [];
                      const closedDays = [];

                      allDays.forEach(day => {
                        const hours = salon.opening_hours[day.key];
                        const isClosed = !hours || hours.ouverture === t('pro_dashboard.salon.closed');
                        if (isClosed) {
                          closedDays.push(day);
                        } else {
                          openDays.push(day);
                        }
                      });

                      return (
                        <>
                          {/* Display open days - responsive layout */}
                          <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            flexWrap: { md: 'wrap' },
                            gap: { xs: 0.8, md: 1 },
                            rowGap: { xs: 0.8, md: 0.5 }
                          }}>
                            {openDays.map((day) => {
                              const hours = salon.opening_hours[day.key];
                              const isToday = day.key === ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'][new Date().getDay()];

                              return (
                                <Typography
                                  key={day.key}
                                  variant="body2"
                                  sx={{
                                    color: 'white',
                                    fontWeight: isToday ? 600 : 400,
                                    fontSize: { xs: '0.9rem', md: '0.875rem' },
                                    width: { xs: '100%', md: 'calc(50% - 4px)' },
                                    lineHeight: 1.4
                                  }}
                                >
                                  {day.label} {hours.ouverture === 'fermé' ? t('pro_dashboard.common.closed') : `${hours.ouverture} - ${hours.fermeture}`}
                                </Typography>
                              );
                            })}
                          </Box>

                          {/* Display closed days */}
                          {closedDays.length > 0 && (
                            <Box sx={{
                              display: 'flex',
                              flexDirection: { xs: 'column', md: 'row' },
                              flexWrap: { md: 'wrap' },
                              gap: { xs: 0.8, md: 1 },
                              rowGap: { xs: 0.8, md: 0.5 },
                              mt: { xs: 1, md: 0.5 }
                            }}>
                              {closedDays.map((day) => {
                                const isToday = day.key === ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'][new Date().getDay()];

                                return (
                                  <Typography
                                    key={day.key}
                                    variant="body2"
                                    sx={{
                                      color: 'rgba(255, 255, 255, 0.7)',
                                      fontWeight: isToday ? 600 : 400,
                                      fontSize: { xs: '0.9rem', md: '0.875rem' },
                                      width: { xs: '100%', md: 'calc(50% - 4px)' },
                                      lineHeight: 1.4
                                    }}
                                  >
                                    {day.label} {t('pro_dashboard.common.closed')}
                                  </Typography>
                                );
                              })}
                            </Box>
                          )}
                        </>
                      );
                    })()}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Content - Gestion du salon */}
      <Container maxWidth="xl" sx={{ pb: { xs: 0, md: 4 }, px: { xs: 0, md: 3 } }}>
        <Card sx={{
          width: '100%',
          backgroundColor: { xs: 'transparent', md: theme.colors.background.primary },
          borderRadius: { xs: 0, md: theme.borderRadius.xl },
          boxShadow: { xs: 'none', md: `0 8px 25px ${theme.colors.primary[50]}` },
          border: { xs: 'none', md: `1px solid ${theme.colors.primary[100]}` },
          overflow: 'hidden'
        }}>
          <CardContent sx={{ p: 0 }}>
            {/* <Box sx={{
              p: 4,
              background: `linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[600]} 100%)`,
              color: theme.colors.background.primary
            }}>
              <Typography 
                variant="h4" 
                sx={{
                  fontWeight: 'bold',
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                <BusinessIcon sx={{ fontSize: '2rem' }} />
                Gestion du salon
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ opacity: 0.9 }}
              >
                {t('pro_dashboard.salon.configure_categories_services')}
              </Typography>
            </Box> */}
            <Box sx={{ p: { xs: 0, md: 4 } }}>
              <SalonManagement
                isUpdateMode={true}
                salonToUpdate={salon}
                onSuccess={(result) => {
                  console.log('Salon updated:', result);
                  refetch();
                }}
              />
            </Box>
          </CardContent>
        </Card>
      </Container>

      {/* Details Dialog - Mobile */}
      <Dialog
        open={showDetailsDialog}
        onClose={() => setShowDetailsDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.25rem' }}>
          {t('pro_dashboard.salon.salon_details')}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Address - only if specified */}
            {getLocalizedAddress(salon) && getLocalizedAddress(salon) !== t('pro_dashboard.salon.not_specified') && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <LocationOnIcon sx={{ color: theme.colors.primary[500], mt: 0.5, fontSize: '1.3rem', flexShrink: 0 }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: theme.colors.text.secondary, mb: 0.5 }}>
                    {t('pro_dashboard.salon.address')}
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.5 }}>
                    {getLocalizedAddress(salon)}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Phone - only if specified */}
            {salon.phone && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <PhoneIcon sx={{ color: theme.colors.primary[500], mt: 0.5, fontSize: '1.3rem', flexShrink: 0 }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: theme.colors.text.secondary, mb: 0.5 }}>
                    {t('pro_dashboard.salon.phone')}
                  </Typography>
                  <Typography
                    variant="body1"
                    component="a"
                    href={`tel:${salon.phone}`}
                    sx={{ lineHeight: 1.5, color: theme.colors.primary[500], textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    {salon.phone}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Opening Hours in Dialog - 2 per line */}
            {salon.opening_hours && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <AccessTimeIcon sx={{ color: theme.colors.primary[500], mt: 0.5, fontSize: '1.3rem', flexShrink: 0 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: theme.colors.text.secondary, mb: 1 }}>
                    {t('pro_dashboard.salon.opening_hours')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {(() => {
                      const daysOrder = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
                      const daysLabels = { lundi: t('weekdays.monday') + ':', mardi: t('weekdays.tuesday') + ':', mercredi: t('weekdays.wednesday') + ':', jeudi: t('weekdays.thursday') + ':', vendredi: t('weekdays.friday') + ':', samedi: t('weekdays.saturday') + ':', dimanche: t('weekdays.sunday') + ':' };

                      return daysOrder.map((day) => {
                        const hours = salon.opening_hours[day];
                        if (!hours || !hours.ouverture || !hours.fermeture) {
                          return (
                            <Typography
                              key={day}
                              variant="body2"
                              sx={{
                                color: theme.colors.text.secondary,
                                fontSize: '0.875rem',
                                width: 'calc(50% - 4px)',
                                lineHeight: 1.6
                              }}
                            >
                              {daysLabels[day]} {t('pro_dashboard.common.closed')}
                            </Typography>
                          );
                        }

                        return (
                          <Typography
                            key={day}
                            variant="body2"
                            sx={{
                              fontSize: '0.875rem',
                              width: 'calc(50% - 4px)',
                              lineHeight: 1.6
                            }}
                          >
                            {daysLabels[day]} {hours.ouverture === 'fermé' ? t('pro_dashboard.common.closed') : `${hours.ouverture} - ${hours.fermeture}`}
                          </Typography>
                        );
                      });
                    })()}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setShowDetailsDialog(false)}
            variant="contained"
            sx={{ bgcolor: theme.colors.primary[500] }}
          >
            {t('pro_dashboard.salon.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reviews Dialog */}
      <Dialog
        open={reviewsDialogOpen}
        onClose={handleCloseReviews}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>{t('pro_dashboard.salon.customer_reviews')}</DialogTitle>
        <DialogContent dividers sx={{ maxHeight: 400 }}>
          {reviewsLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                {t('pro_dashboard.salon.loading_reviews')}
              </Typography>
            </Box>
          )}

          {reviewsError && !reviewsLoading && (
            <Typography variant="body2" color="error">
              {reviewsError}
            </Typography>
          )}

          {!reviewsLoading && !reviewsError && reviews && reviews.length === 0 && (
            <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
              {t('pro_dashboard.salon.no_reviews_yet')}
            </Typography>
          )}

          {!reviewsLoading && !reviewsError && reviews && reviews.length > 0 && (
            <>
              {(() => {
                const averageRating =
                  reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviews.length;
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Rating
                      name="salon-rating-pro-detail"
                      value={averageRating}
                      precision={0.5}
                      readOnly
                      size="small"
                    />
                    <Typography
                      variant="body2"
                      sx={{ ml: 1, color: theme.colors.text.secondary }}
                    >
                      {averageRating.toFixed(1)} / 5 · {reviews.length} {t('pro_dashboard.common.reviews')}
                    </Typography>
                  </Box>
                );
              })()}

              <List dense sx={{ p: 0 }}>
                {reviews.map((review) => (
                  <ListItem
                    key={review.review_id}
                    alignItems="flex-start"
                    disableGutters
                    sx={{ pb: 1.5 }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2">
                            {review.client_name || t('pro_dashboard.common.client')}
                          </Typography>
                          <Rating
                            value={review.rating}
                            readOnly
                            size="small"
                          />
                        </Box>
                      }
                      secondary={
                        <>
                          {review.comment && (
                            <Typography
                              variant="body2"
                              sx={{ color: theme.colors.text.secondary }}
                            >
                              {review.comment}
                            </Typography>
                          )}
                          {review.created_at && (
                            <Typography
                              variant="caption"
                              sx={{ color: theme.colors.grey[600] }}
                            >
                              {new Date(review.created_at).toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : i18n.language === 'en' ? 'en-US' : 'fr-FR')}
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseReviews}
            variant="contained"
            sx={{ bgcolor: theme.colors.primary[500] }}
          >
            {t('pro_dashboard.salon.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>{t('pro_dashboard.salon.qr_code_title')}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {isQrLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : qrError ? (
            <Alert severity="error">{t('pro_dashboard.salon.qr_generate_error')}</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
              {qrDataUri ? (
                <Box
                  sx={{
                    width: 260,
                    height: 260,
                    borderRadius: 2,
                    border: `1px solid ${theme.colors.grey?.[200] || '#eee'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'white',
                    overflow: 'hidden'
                  }}
                >
                  <img src={qrDataUri} alt="QR code" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </Box>
              ) : null}

              <TextField
                value={publicUrl}
                label={t('pro_dashboard.salon.public_link')}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
              />

              <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<ContentCopyIcon />}
                  onClick={handleCopyPublicUrl}
                  disabled={!publicUrl}
                >
                  {t('pro_dashboard.common.copy')}
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<DownloadIcon />}
                  component="a"
                  href={qrDataUri || '#'}
                  download={qrDownloadFilename}
                  disabled={!qrDataUri}
                >
                  {t('pro_dashboard.common.download')}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setQrDialogOpen(false)} variant="contained" sx={{ bgcolor: theme.colors.primary[500] }}>
            {t('pro_dashboard.salon.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <AddSalon
        open={showEditDialog}
        focusPhotos={focusEditDialogPhotos}
        onPhotoFocusConsumed={() => setFocusEditDialogPhotos(false)}
        onClose={() => {
          setFocusEditDialogPhotos(false);
          setShowEditDialog(false);
          // Reste sur SalonDetail au lieu de naviguer
        }}
        onSuccess={(updatedSalon) => {
          if (updatedSalon) {
            refetch();
            setFocusEditDialogPhotos(false);
            setShowEditDialog(false);
            // Reste sur SalonDetail pour voir les modifications
            setNotification({
              open: true,
              message: t('pro_dashboard.salon.modification_updated'),
              severity: 'success',
            });
          }
        }}
        isUpdateMode={true}
        salonToUpdate={salon}
      />

      {/* Success notification for salon update */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>

      <TranslationValidationModal
        open={translationModalOpen}
        onClose={() => setTranslationModalOpen(false)}
        onValidate={handleSaveTranslations}
        salonName={salon?.name || ''}
        salonDescription={salon?.description || ''}
        isLoading={translationModalLoading}
        translations={salonTranslations}
      />
    </Box>
  );
};

export default SalonDetail;
